// SPDX-FileCopyrightText: 2024 Chris Duncan <chris@zoso.dev>
// SPDX-License-Identifier: GPL-3.0-or-later
import { Account } from './account.js';
/**
* Represents a basic address book of Nano accounts. Multiple addresses can be
* saved under one nickname.
*/
export class Rolodex {
    #entries = [];
    /**
    * Adds an address to the rolodex under a specific nickname.
    *
    * If the name exists, add the address as a new association to that name. If
    * the account exists under a different name, update the name. If no matches
    * are found at all, add a completely new entry.
    *
    * @param {string} name - Alias for the address
    * @param {string} address - Nano account address
    */
    async add(name, address) {
        if (name == null || name === '') {
            throw new Error('Name is required for rolodex entries');
        }
        if (typeof name !== 'string') {
            throw new Error('Name must be a string for rolodex entries');
        }
        if (address == null || address === '') {
            throw new Error('Address is required for rolodex entries');
        }
        if (typeof address !== 'string') {
            throw new Error('Address must be a string for rolodex entries');
        }
        name = name
            .replaceAll('/', '\\u002f')
            .replaceAll('<', '\\u003c')
            .replaceAll('>', '\\u003d')
            .replaceAll('\\', '\\u005c');
        const account = new Account(address);
        const nameResult = this.#entries.find(e => e.name === name);
        const accountResult = this.#entries.find(e => e.account.address === address);
        if (!accountResult) {
            this.#entries.push({ name, account });
        }
        else if (!nameResult) {
            accountResult.name = name;
        }
    }
    /**
    * Gets the name associated with a specific Nano address from the rolodex.
    *
    * @param {string} address - Nano account address
    * @returns {string|null} Name associated with the address, or null if not found
    */
    getName(address) {
        const result = this.#entries.find(e => e.account.address === address);
        return result?.name ?? null;
    }
    /**
    * Gets all Nano addresses associated with a name from the rolodex.
    *
    * @param {string} name - Alias to look up
    * @returns {string[]} List of Nano addresses associated with the name
    */
    getAddresses(name) {
        const entries = this.#entries.filter(e => e.name === name);
        return entries.map(a => a.account.address);
    }
    /**
    * Gets all names stored in the rolodex.
    *
    * @returns {string[]} List of names stored in the rolodex
    */
    getAllNames() {
        return this.#entries.map(e => e.name);
    }
    /**
    * Verifies whether the public key of any Nano address saved under a specific
    * name in the rolodex was used to sign a specific set of data.
    *
    * @param {string} name - Alias to look up
    * @param {string} signature - Signature to use for verification
    * @param {...string} data - Signed data to verify
    * @returns {boolean} True if the signature was used to sign the data, else false
    */
    async verify(name, signature, ...data) {
        const Tools = await import('./tools.js');
        const entries = this.#entries.filter(e => e.name === name);
        for (const entry of entries) {
            const key = entry.account.publicKey;
            const verified = await Tools.verify(key, signature, ...data);
            if (verified) {
                return true;
            }
        }
        return false;
    }
}
